import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Clock,
  Copy,
  Loader2,
  Mail,
  Pause,
  Pencil,
  Play,
  Plus,
  RotateCcw,
  Send,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { Footer } from '@/components/footer'
import { NavBar } from '@/components/nav-bar'
import {
  addTenantMember,
  cancelTenantDeletion,
  getCurrentUser,
  getMyTenants,
  getTenantInvites,
  getTenantMembers,
  PlatformApiError,
  type PlatformTenant,
  type PlatformUser,
  pauseTenant,
  removeTenantMember,
  renameTenant,
  resumeTenant,
  revokeTenantInvite,
  scheduleTenantDeletion,
  type TenantInvite,
  type TenantMember,
} from '@/lib/api'
import { type ClassifiedError, classifyError, ErrorBanner } from '@/lib/errors'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/account/workspaces/$tenantId')({
  component: TenantMembersPage,
})

/**
 * Workspace settings page — manages workspace managers and lifecycle.
 * Lives under /account (not /platform) because this is a customer-
 * facing flow — any manager of a workspace can access it without
 * being a platform admin.
 *
 * "Workspace managers" are people who can access this page, manage
 * billing, and control the workspace lifecycle. They are NOT the
 * same as ERP team members (employees) — those are managed inside
 * the ERP at /settings/users.
 *
 * Fetches three things in parallel on mount:
 *   - /me → to disable the self-row remove button
 *   - /tenants → to look up the tenant metadata (name, slug)
 *   - /tenants/:id/members → the managers
 *
 * Access gate is server-side: the backend returns 403 for
 * non-members. We show that as a friendly forbidden state so the
 * page doesn't appear broken.
 */
function TenantMembersPage() {
  const { tenantId } = Route.useParams()
  const navigate = useNavigate()

  const [state, setState] = useState<
    | { status: 'loading' }
    | {
        status: 'loaded'
        me: PlatformUser
        tenant: PlatformTenant | null
        members: TenantMember[]
        invites: TenantInvite[]
      }
    | { status: 'forbidden' }
    | { status: 'error'; message: string }
  >({ status: 'loading' })

  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  useEffect(() => {
    const ctrl = new AbortController()
    Promise.all([
      getCurrentUser(ctrl.signal),
      getMyTenants(ctrl.signal),
      getTenantMembers(tenantId, ctrl.signal),
      getTenantInvites(tenantId, ctrl.signal),
    ])
      .then(([me, tenants, members, invites]) => {
        const tenant = tenants.find((t) => t.id === tenantId) ?? null
        setState({ status: 'loaded', me, tenant, members, invites })
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        if (err instanceof PlatformApiError && err.status === 401) {
          void navigateRef.current({ to: '/sign-in' })
          return
        }
        if (err instanceof PlatformApiError && err.status === 403) {
          setState({ status: 'forbidden' })
          return
        }
        const message =
          err instanceof PlatformApiError && err.message
            ? err.message
            : 'Failed to load workspace'
        setState({ status: 'error', message })
      })
    return () => ctrl.abort()
  }, [tenantId])

  return (
    <>
      <NavBar />
      <main className="flex min-h-screen flex-col">
        <section className="relative flex-1 pt-32 pb-24">
          <div
            className="pointer-events-none absolute inset-0 -z-10 marketing-grid-bg opacity-20"
            aria-hidden="true"
          />
          <div className="mx-auto w-full max-w-3xl px-4 sm:px-6">
            <Link
              to="/account"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to account
            </Link>

            {state.status === 'loading' ? <LoadingState /> : null}
            {state.status === 'forbidden' ? <ForbiddenState /> : null}
            {state.status === 'error' ? <ErrorState message={state.message} /> : null}
            {state.status === 'loaded' ? (
              <LoadedView
                tenantId={tenantId}
                tenant={state.tenant}
                me={state.me}
                members={state.members}
                invites={state.invites}
                onMembersChange={(members) =>
                  setState((s) => (s.status === 'loaded' ? { ...s, members } : s))
                }
                onInvitesChange={(invites) =>
                  setState((s) => (s.status === 'loaded' ? { ...s, invites } : s))
                }
                onTenantChange={(tenant) =>
                  setState((s) => (s.status === 'loaded' ? { ...s, tenant } : s))
                }
              />
            ) : null}
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}

function LoadingState() {
  return (
    <div className="mt-10 flex items-center justify-center gap-2 py-16 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading workspace…
    </div>
  )
}

function ForbiddenState() {
  return (
    <div className="mt-10 rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
      You are not a manager of this workspace, so you can't access its settings.
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="mt-10 rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
      {message}
    </div>
  )
}

function LoadedView({
  tenantId,
  tenant,
  me,
  members,
  invites,
  onMembersChange,
  onInvitesChange,
  onTenantChange,
}: {
  tenantId: string
  tenant: PlatformTenant | null
  me: PlatformUser
  members: TenantMember[]
  invites: TenantInvite[]
  onMembersChange: (members: TenantMember[]) => void
  onInvitesChange: (invites: TenantInvite[]) => void
  onTenantChange: (tenant: PlatformTenant) => void
}) {
  // Store the classified error so the render can pick a tone
  // (warning banner for rate_limit, destructive banner for
  // server/client errors) without re-classifying at render time.
  const [addError, setAddError] = useState<ClassifiedError | null>(null)
  const [addBusy, setAddBusy] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [removeError, setRemoveError] = useState<string | null>(null)
  // When an invite is created, we surface the copyable URL inline
  // for a few seconds so the inviter can grab it immediately.
  // Holds the most recent invite URL created in this session.
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [revokingInviteId, setRevokingInviteId] = useState<string | null>(null)

  // Rename state — null when not editing. Keeps the rename UI out
  // of the DOM entirely until requested, so the page header stays
  // the simpler read-only shape by default.
  const [renameDraft, setRenameDraft] = useState<string | null>(null)
  const [renameBusy, setRenameBusy] = useState(false)
  const [renameError, setRenameError] = useState<string | null>(null)

  // Delete-confirm dialog + in-flight + error.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Pause / resume / cancel-deletion share a single busy flag —
  // the three actions are mutually exclusive by state anyway
  // (pause only from active, resume only from paused, cancel only
  // from pending_deletion), so one flag prevents double-clicks
  // without needing per-action tracking.
  const [lifecycleBusy, setLifecycleBusy] = useState(false)
  const [lifecycleError, setLifecycleError] = useState<string | null>(null)

  const tenantStatus = tenant?.status ?? 'active'
  const isActive = tenantStatus === 'active'
  const isPaused = tenantStatus === 'paused'
  const isPending = tenantStatus === 'pending_deletion'
  const isReadOnly = isPaused || isPending

  async function handleAdd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    const formData = new FormData(form)
    const email = String(formData.get('email') ?? '').trim()
    if (!email) return

    setAddBusy(true)
    setAddError(null)
    setLastInviteUrl(null)
    setCopied(false)
    try {
      const result = await addTenantMember(tenantId, email)
      if (result.status === 'joined') {
        onMembersChange([...members, result.member])
      } else {
        // Invite branch — push it into the pending invites list so
        // it renders below the members immediately, AND stash the
        // URL in lastInviteUrl so we can show the copy panel inline.
        onInvitesChange([
          ...invites,
          {
            id: result.invite.id,
            email: result.invite.email,
            role: result.invite.role,
            invitedByEmail: me.email,
            expiresAt: result.invite.expiresAt,
            createdAt: result.invite.createdAt,
          },
        ])
        setLastInviteUrl(result.invite.inviteUrl)
      }
      form.reset()
    } catch (err) {
      setAddError(classifyError(err))
    } finally {
      setAddBusy(false)
    }
  }

  async function handleCopyInvite(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard permission denied — the URL is still visible in
      // the UI so the user can hand-copy it. No fallback needed.
    }
  }

  async function handleRevokeInvite(inviteId: string, email: string) {
    if (!confirm(`Revoke invite for ${email}? The sign-up link will stop working.`)) return
    setRevokingInviteId(inviteId)
    try {
      await revokeTenantInvite(tenantId, inviteId)
      onInvitesChange(invites.filter((i) => i.id !== inviteId))
      // If we were showing this invite's URL inline, clear it too.
      setLastInviteUrl(null)
    } catch (err) {
      // Revoke errors surface through the same banner as invite-add
      // errors — they're both "manage this team" failures and the
      // banner lives right above the list where the user triggered
      // the action. Classified the same way for consistency.
      setAddError(classifyError(err))
    } finally {
      setRevokingInviteId(null)
    }
  }

  async function handleRemove(userId: string, email: string) {
    if (!confirm(`Remove ${email} from this workspace?`)) return
    setRemovingId(userId)
    setRemoveError(null)
    try {
      await removeTenantMember(tenantId, userId)
      onMembersChange(members.filter((m) => m.userId !== userId))
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to remove member'
      setRemoveError(message)
    } finally {
      setRemovingId(null)
    }
  }

  async function handleRenameSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (renameDraft === null) return
    const trimmed = renameDraft.trim()
    if (!trimmed || trimmed === tenant?.name) {
      setRenameDraft(null)
      setRenameError(null)
      return
    }
    setRenameBusy(true)
    setRenameError(null)
    try {
      const updated = await renameTenant(tenantId, trimmed)
      onTenantChange(updated)
      setRenameDraft(null)
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to rename workspace'
      setRenameError(message)
    } finally {
      setRenameBusy(false)
    }
  }

  async function handleDelete() {
    setDeleteBusy(true)
    setDeleteError(null)
    try {
      const updated = await scheduleTenantDeletion(tenantId)
      // Stay on the page so the countdown + cancel button are
      // immediately visible — this isn't a "bye forever" action.
      if (tenant) {
        onTenantChange({
          ...tenant,
          status: updated.status,
          scheduledDeleteAt: updated.scheduledDeleteAt,
        })
      }
      setShowDeleteConfirm(false)
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to delete workspace'
      setDeleteError(message)
    } finally {
      setDeleteBusy(false)
    }
  }

  async function handlePause() {
    if (
      !confirm(
        `Pause ${tenant?.name ?? 'this workspace'}? Members will lose write access until you resume.`,
      )
    )
      return
    setLifecycleBusy(true)
    setLifecycleError(null)
    try {
      const updated = await pauseTenant(tenantId)
      if (tenant) onTenantChange({ ...tenant, status: updated.status })
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to pause workspace'
      setLifecycleError(message)
    } finally {
      setLifecycleBusy(false)
    }
  }

  async function handleResume() {
    setLifecycleBusy(true)
    setLifecycleError(null)
    try {
      const updated = await resumeTenant(tenantId)
      if (tenant) onTenantChange({ ...tenant, status: updated.status })
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to resume workspace'
      setLifecycleError(message)
    } finally {
      setLifecycleBusy(false)
    }
  }

  async function handleCancelDeletion() {
    setLifecycleBusy(true)
    setLifecycleError(null)
    try {
      const updated = await cancelTenantDeletion(tenantId)
      if (tenant) {
        onTenantChange({
          ...tenant,
          status: updated.status,
          scheduledDeleteAt: updated.scheduledDeleteAt,
        })
      }
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to cancel deletion'
      setLifecycleError(message)
    } finally {
      setLifecycleBusy(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="mt-8"
    >
      <header className="mb-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/50 px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground backdrop-blur">
          <Users className="h-3 w-3" />
          Workspace settings
        </div>
        {renameDraft === null ? (
          <div className="mt-2 flex items-center gap-2">
            <h1 className="text-balance text-2xl font-bold tracking-tight sm:text-3xl">
              <span className="marketing-gradient-text">{tenant?.name ?? 'Workspace'}</span>
            </h1>
            <button
              type="button"
              onClick={() => setRenameDraft(tenant?.name ?? '')}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground',
                'hover:bg-accent hover:text-foreground transition-colors',
              )}
              title="Rename this workspace"
            >
              <Pencil className="h-3 w-3" />
              Rename
            </button>
          </div>
        ) : (
          <form onSubmit={handleRenameSubmit} className="mt-2 flex items-center gap-2">
            <input
              ref={(el) => {
                // Auto-focus via ref instead of the autoFocus attr
                // so the a11y/noAutofocus lint rule stays happy.
                el?.focus()
              }}
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              maxLength={100}
              disabled={renameBusy}
              className={cn(
                'h-10 flex-1 rounded-full border border-border bg-background px-4 text-lg font-semibold tracking-tight',
                'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
                'disabled:opacity-60 disabled:cursor-not-allowed',
              )}
            />
            <button
              type="submit"
              disabled={renameBusy}
              className={cn(
                'inline-flex h-10 items-center gap-1 rounded-lg px-4 text-xs font-semibold',
                'bg-primary text-primary-foreground shadow-md shadow-primary/20',
                'hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed',
              )}
            >
              {renameBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setRenameDraft(null)
                setRenameError(null)
              }}
              disabled={renameBusy}
              className={cn(
                'inline-flex h-10 items-center gap-1 rounded-full border border-border px-3 text-xs font-medium',
                'hover:bg-accent hover:text-accent-foreground transition-colors',
                'disabled:opacity-60 disabled:cursor-not-allowed',
              )}
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
          </form>
        )}
        {renameError ? (
          <div className="mt-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-1.5 text-xs text-destructive">
            {renameError}
          </div>
        ) : null}
        <p className="mt-3 text-sm text-muted-foreground">
          Manage your team and workspace settings. Changes apply only to the marketing-side
          registry; the ERP database and its data stay untouched.
        </p>
      </header>

      {/* Status banner — only rendered when the workspace is in a
          non-active state. Drives the user toward the recovery
          action (Resume or Cancel deletion). */}
      {isReadOnly ? (
        <StatusBanner
          status={tenantStatus}
          scheduledDeleteAt={tenant?.scheduledDeleteAt ?? null}
          busy={lifecycleBusy}
          error={lifecycleError}
          onResume={handleResume}
          onCancelDeletion={handleCancelDeletion}
        />
      ) : null}

      {/* "Manage team" card — directs to the ERP for employee management */}
      <section className="rounded-2xl border border-primary/20 bg-primary/5 p-6 shadow-sm backdrop-blur-md">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <Users className="h-4 w-4" />
              Team management
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Employees and their roles are managed inside the ERP itself, under Settings &rarr;
              Users. Sign into the workspace to add, edit, or remove team members.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              // SSO into the workspace — requestSsoHandoff returns a
              // URL the browser follows to pick up the tenant session.
              import('@/lib/api').then(({ requestSsoHandoff }) =>
                requestSsoHandoff(tenantId).then(({ url }) => {
                  window.location.href = url
                }),
              )
            }}
            className={cn(
              'inline-flex flex-none items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium',
              'bg-primary text-primary-foreground shadow-md shadow-primary/20',
              'hover:opacity-90 transition-opacity',
            )}
          >
            Open workspace
          </button>
        </div>
      </section>

      <section className="mt-6 rounded-2xl border border-border bg-card/80 p-6 shadow-sm backdrop-blur-md">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <UserPlus className="h-4 w-4" />
          Add a workspace manager
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Workspace managers can access this page, manage billing, and control the workspace
          lifecycle (pause, resume, delete). This is separate from ERP team members — add regular
          employees through the ERP&apos;s user management instead.
        </p>
        <form onSubmit={handleAdd} className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start">
          <div className="flex-1">
            <label htmlFor="member-email" className="sr-only">
              Email address
            </label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                id="member-email"
                name="email"
                type="email"
                required
                placeholder="person@example.com"
                disabled={addBusy}
                className={cn(
                  'h-10 w-full rounded-full border border-border bg-background pl-9 pr-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
                  'disabled:opacity-60 disabled:cursor-not-allowed',
                )}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={addBusy}
            className={cn(
              'inline-flex h-10 items-center gap-1.5 rounded-lg px-5 text-sm font-medium',
              'bg-primary text-primary-foreground shadow-md shadow-primary/20',
              'hover:opacity-90 transition-opacity',
              'disabled:opacity-60 disabled:cursor-not-allowed',
            )}
          >
            {addBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {addBusy ? 'Adding…' : 'Add manager'}
          </button>
        </form>
        {addError ? <ErrorBanner error={addError} /> : null}
        {lastInviteUrl ? (
          <div className="mt-3 rounded-md border border-primary/30 bg-primary/5 p-3 text-xs">
            <div className="flex items-center gap-2 font-semibold text-primary">
              <Send className="h-3.5 w-3.5" />
              Invite created
            </div>
            <p className="mt-1 text-muted-foreground">
              The backend logged the invite email to the server console. Copy this link and send it
              through your preferred channel:
            </p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-background px-2 py-1 text-[11px]">
                {lastInviteUrl}
              </code>
              <button
                type="button"
                onClick={() => handleCopyInvite(lastInviteUrl)}
                className={cn(
                  'inline-flex h-7 flex-none items-center gap-1 rounded-md border border-border px-2 text-[11px] font-medium',
                  'hover:bg-accent hover:text-accent-foreground transition-colors',
                )}
              >
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        ) : null}
      </section>

      {invites.length > 0 ? (
        <section className="mt-6 rounded-2xl border border-border bg-card/80 p-6 shadow-sm backdrop-blur-md">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Send className="h-4 w-4" />
            Pending invites ({invites.length})
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            People you've invited but who haven't accepted yet. Revoking an invite invalidates the
            sign-up link immediately.
          </p>
          <ul className="mt-4 divide-y divide-border">
            {invites.map((invite) => {
              const expired = new Date(invite.expiresAt).getTime() < Date.now()
              const revoking = revokingInviteId === invite.id
              return (
                <li
                  key={invite.id}
                  className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="truncate font-medium">{invite.email}</div>
                      <span className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground ring-1 ring-border">
                        {invite.role}
                      </span>
                      {expired ? (
                        <span className="inline-flex items-center rounded-full bg-destructive/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-destructive ring-1 ring-destructive/25">
                          Expired
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      Invited {formatInviteDate(invite.createdAt)} · expires{' '}
                      {formatInviteDate(invite.expiresAt)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRevokeInvite(invite.id, invite.email)}
                    disabled={revoking}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium',
                      'hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 transition-colors',
                      'disabled:opacity-40 disabled:cursor-not-allowed',
                    )}
                  >
                    {revoking ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                    {revoking ? 'Revoking…' : 'Revoke'}
                  </button>
                </li>
              )
            })}
          </ul>
        </section>
      ) : null}

      <section className="mt-6 rounded-2xl border border-border bg-card/80 p-6 shadow-sm backdrop-blur-md">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Users className="h-4 w-4" />
            Workspace managers ({members.length})
          </h2>
        </div>

        {removeError ? (
          <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {removeError}
          </div>
        ) : null}

        <ul className="mt-4 divide-y divide-border">
          {members.map((member) => {
            const isSelf = member.userId === me.id
            const busy = removingId === member.userId
            return (
              <li
                key={member.userId}
                className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="truncate font-medium">{member.name ?? member.email}</div>
                    {isSelf ? (
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary ring-1 ring-primary/25">
                        You
                      </span>
                    ) : null}
                    <span className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground ring-1 ring-border">
                      {member.role}
                    </span>
                  </div>
                  {member.name ? (
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {member.email}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(member.userId, member.email)}
                  disabled={isSelf || busy}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium',
                    'hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 transition-colors',
                    'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-inherit disabled:hover:border-border',
                  )}
                  title={
                    isSelf ? "You can't remove yourself — ask another member" : 'Remove member'
                  }
                >
                  {busy ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3" />
                  )}
                  {busy ? 'Removing…' : 'Remove'}
                </button>
              </li>
            )
          })}
        </ul>
      </section>

      {/* Lifecycle actions — the "pause this workspace" door.
          Only rendered when the workspace is active. Paused and
          pending_deletion states surface their own recovery UI in
          the status banner above. */}
      {isActive ? (
        <section className="mt-6 rounded-2xl border border-border bg-card/60 p-6">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Pause className="h-4 w-4" />
            Pause workspace
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Temporarily freeze the workspace in read-only mode. Members can still sign in and
            browse, but no one can make changes. Resume any time.
          </p>
          {lifecycleError ? (
            <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-1.5 text-xs text-destructive">
              {lifecycleError}
            </div>
          ) : null}
          <div className="mt-4">
            <button
              type="button"
              onClick={handlePause}
              disabled={lifecycleBusy}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-semibold',
                'hover:bg-accent hover:text-accent-foreground transition-colors',
                'disabled:opacity-60 disabled:cursor-not-allowed',
              )}
            >
              {lifecycleBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Pause className="h-3.5 w-3.5" />
              )}
              Pause workspace
            </button>
          </div>
        </section>
      ) : null}

      {/* Danger zone — schedule deletion. Hidden when the workspace
          is already in pending_deletion (the status banner above
          owns the Cancel deletion action in that case). */}
      {!isPending ? (
        <section className="mt-6 rounded-2xl border border-destructive/30 bg-destructive/5 p-6">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-destructive">
            <AlertTriangle className="h-4 w-4" />
            Danger zone
          </h2>
          <p className="mt-1 text-xs text-destructive/80">
            Schedule this workspace for deletion. It enters a {DELETE_GRACE_DAYS}-day read-only
            grace period so your team can export data. After that, the ERP database is permanently
            dropped. You can cancel the deletion any time during the grace window.
          </p>
          {deleteError ? (
            <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
              {deleteError}
            </div>
          ) : null}
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={deleteBusy}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border border-destructive/40 bg-destructive/10 px-4 py-2 text-xs font-semibold text-destructive',
                'hover:bg-destructive/20 transition-colors',
                'disabled:opacity-60 disabled:cursor-not-allowed',
              )}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete workspace…
            </button>
          </div>
        </section>
      ) : null}

      {showDeleteConfirm ? (
        <DeleteWorkspaceDialog
          tenantName={tenant?.name ?? 'this workspace'}
          memberCount={members.length}
          graceDays={DELETE_GRACE_DAYS}
          busy={deleteBusy}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      ) : null}
    </motion.div>
  )
}

/**
 * Length of the post-delete grace window, in days. Mirrors the
 * backend's DELETE_GRACE_DAYS constant — keep them in sync. Copied
 * here rather than passed as a prop so the component can render
 * without a server round trip.
 */
const DELETE_GRACE_DAYS = 30

/**
 * In-page banner shown when the tenant is paused or pending
 * deletion. Explains the state, shows the countdown for pending
 * deletions, and owns the recovery button.
 */
function StatusBanner({
  status,
  scheduledDeleteAt,
  busy,
  error,
  onResume,
  onCancelDeletion,
}: {
  status: 'paused' | 'pending_deletion'
  scheduledDeleteAt: string | null
  busy: boolean
  error: string | null
  onResume: () => void
  onCancelDeletion: () => void
}) {
  const isPaused = status === 'paused'
  const tone = isPaused
    ? 'border-warning/40 bg-warning/5 text-warning'
    : 'border-destructive/40 bg-destructive/5 text-destructive'
  const Icon = isPaused ? Pause : Clock

  const countdown = isPaused ? null : scheduledDeleteAt ? formatCountdown(scheduledDeleteAt) : null

  return (
    <div className={cn('mb-6 rounded-2xl border p-5', tone)}>
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'mt-0.5 inline-flex h-9 w-9 flex-none items-center justify-center rounded-full ring-1',
            isPaused ? 'bg-warning/10 ring-warning/25' : 'bg-destructive/10 ring-destructive/25',
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">
            {isPaused ? 'Workspace is paused' : 'Workspace is scheduled for deletion'}
          </h3>
          <p className="mt-1 text-xs opacity-90">
            {isPaused
              ? 'Members can sign in and browse, but writes are blocked until you resume.'
              : countdown
                ? `Read-only. ${countdown} until the ERP database is permanently dropped. Cancel the deletion to return the workspace to active.`
                : 'Read-only. Cancel the deletion to return the workspace to active.'}
          </p>
          {error ? (
            <div className="mt-2 rounded-md border border-destructive/40 bg-background/50 px-3 py-1.5 text-xs text-destructive">
              {error}
            </div>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            {isPaused ? (
              <button
                type="button"
                onClick={onResume}
                disabled={busy}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-xs font-semibold',
                  // text-white explicitly — the theme doesn't define
                  // --warning-foreground, so text-warning-foreground
                  // resolves to nothing and the button inherits
                  // text-warning from the parent banner.
                  'bg-warning !text-white shadow-md shadow-warning/20',
                  'hover:opacity-90 transition-opacity',
                  'disabled:opacity-60 disabled:cursor-not-allowed',
                )}
              >
                {busy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
                Resume workspace
              </button>
            ) : (
              <button
                type="button"
                onClick={onCancelDeletion}
                disabled={busy}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-xs font-semibold',
                  'bg-destructive text-destructive-foreground shadow-md shadow-destructive/20',
                  'hover:opacity-90 transition-opacity',
                  'disabled:opacity-60 disabled:cursor-not-allowed',
                )}
              >
                {busy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="h-3.5 w-3.5" />
                )}
                Cancel deletion
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Humanize the time remaining until a future timestamp. Used by
 * StatusBanner for the "27 days left" copy. Doesn't auto-update
 * — the page reloads on navigation, which is good enough.
 */
function formatCountdown(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now()
  if (Number.isNaN(ms) || ms <= 0) return 'Grace window expired'
  const days = Math.floor(ms / (24 * 60 * 60 * 1000))
  if (days >= 2) return `${days} days left`
  if (days === 1) return '1 day left'
  const hours = Math.max(1, Math.floor(ms / (60 * 60 * 1000)))
  return `${hours} hour${hours === 1 ? '' : 's'} left`
}

/**
 * Short date format for invite rows — "Apr 15" or "Apr 15, 2025"
 * if the year differs from the current one.
 */
function formatInviteDate(iso: string): string {
  try {
    const d = new Date(iso)
    const thisYear = new Date().getFullYear()
    const opts: Intl.DateTimeFormatOptions =
      d.getFullYear() === thisYear
        ? { month: 'short', day: 'numeric' }
        : { month: 'short', day: 'numeric', year: 'numeric' }
    return d.toLocaleDateString(undefined, opts)
  } catch {
    return iso
  }
}

/**
 * Destructive confirm dialog for scheduling a workspace deletion.
 *
 * Friction is deliberately high:
 *   - Forces the user to type the workspace name EXACTLY (case
 *     sensitive) to unlock the Delete button.
 *   - Lists the consequences in plain language so nobody is
 *     surprised by the 30-day grace window or read-only mode.
 *   - Shows the member count so collective impact is visible.
 *
 * On confirm, the workspace transitions to `pending_deletion` and
 * the user stays on the page — the status banner above the dialog
 * takes over with the countdown and cancel button. No navigation
 * so a mistake is one click away from being undone.
 */
function DeleteWorkspaceDialog({
  tenantName,
  memberCount,
  graceDays,
  busy,
  onConfirm,
  onCancel,
}: {
  tenantName: string
  memberCount: number
  graceDays: number
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const [typed, setTyped] = useState('')
  const canConfirm = typed === tenantName && !busy

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onCancel}
        disabled={busy}
        className="absolute inset-0 h-full w-full cursor-default bg-background/80 backdrop-blur-sm disabled:cursor-not-allowed"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-lg rounded-2xl border border-destructive/40 bg-card p-6 shadow-xl shadow-destructive/10"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center gap-2">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive ring-1 ring-destructive/25">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold tracking-tight">Schedule workspace deletion</h3>
            <p className="text-xs text-muted-foreground">
              This action is reversible for {graceDays} days, then permanent.
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-2 rounded-xl border border-border bg-background/50 p-4 text-xs text-muted-foreground">
          <p className="font-semibold text-foreground">What happens next:</p>
          <ul className="space-y-1.5">
            <li className="flex items-start gap-2">
              <Clock className="mt-0.5 h-3 w-3 flex-none text-warning" />
              <span>
                <strong className="text-foreground">Immediately:</strong> {tenantName} enters
                read-only mode. All {memberCount} {memberCount === 1 ? 'member' : 'members'} lose
                write access, but can still sign in to export data, run reports, and copy records.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <RotateCcw className="mt-0.5 h-3 w-3 flex-none text-success" />
              <span>
                <strong className="text-foreground">For {graceDays} days:</strong> You can cancel
                the deletion from this page and everything goes back to normal — zero data loss.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Trash2 className="mt-0.5 h-3 w-3 flex-none text-destructive" />
              <span>
                <strong className="text-foreground">After {graceDays} days:</strong> The ERP
                database is permanently dropped. Records, users, and file attachments cannot be
                recovered. Platform-side metadata (members, licenses, billing history) is retained
                for audit.
              </span>
            </li>
          </ul>
        </div>

        <div className="mt-5">
          <label htmlFor="confirm-name" className="text-xs font-medium text-muted-foreground">
            Type the workspace name to confirm:{' '}
            <code className="rounded bg-muted px-1 py-0.5 text-[11px] text-foreground">
              {tenantName}
            </code>
          </label>
          <input
            id="confirm-name"
            ref={(el) => {
              el?.focus()
            }}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={busy}
            autoComplete="off"
            spellCheck={false}
            className={cn(
              'mt-1.5 h-10 w-full rounded-full border border-border bg-background px-4 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-destructive/50 focus:ring-offset-2 focus:ring-offset-background',
              'disabled:opacity-60 disabled:cursor-not-allowed',
              typed && typed !== tenantName && 'border-destructive/60',
            )}
          />
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={cn(
              'inline-flex items-center rounded-full border border-border px-4 py-2 text-sm font-medium',
              'hover:bg-accent hover:text-accent-foreground transition-colors',
              'disabled:opacity-60 disabled:cursor-not-allowed',
            )}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold',
              'bg-destructive text-destructive-foreground shadow-md shadow-destructive/20',
              'hover:opacity-90 transition-opacity',
              'disabled:opacity-40 disabled:cursor-not-allowed',
            )}
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            {busy ? 'Scheduling…' : `Schedule deletion in ${graceDays} days`}
          </button>
        </div>
      </motion.div>
    </div>
  )
}
